/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * DECISION BOARD CHECK RESOLUTIONS (v1_737).
 *
 * A blocking decision-board check clears from evidence in the report. Some
 * checks cannot: "Are they regulated?" for a genuinely unregulated company had
 * only one done-path — regulated === false AND an mlro_decision — but the
 * decision is exactly what the check blocks, a circular deadlock the board
 * created; and a source-of-funds answer the officer accepts had no control at
 * all. So an explicit officer resolution, recorded in
 * collected_data.checkResolutions keyed by the check's key, also clears a check.
 *
 * Pure so the done-logic is testable: applied once inside the decision-item
 * computation, and the source-of-funds/wealth resolution additionally accepts
 * the assessment score (handled by the caller) so the money-flow map clears too.
 */

// Mutates the checks in place (they are freshly built each computation) and
// returns them: a check with a recorded officer resolution is marked done and
// carries the resolution for display.
export function applyCheckResolutions(checks, checkResolutions) {
  const res = checkResolutions || {};
  for (const c of (Array.isArray(checks) ? checks : [])) {
    if (c && !c.done && c.key && res[c.key]) {
      c.done = true;
      c.officerResolved = res[c.key];
    }
  }
  return checks;
}

/* ── ONE RECONCILED SOURCE FOR OUTSTANDING (v1_743) ─────────────────────────
 *
 * The decision board's exceptions and the rationale fact pack must read the
 * SAME reconciled state the officer sees: a document gap whose request item is
 * resolved or withdrawn is closed; a document the officer accepted is not an
 * exception; a check the officer resolved is done; and the fact pack's
 * "outstanding" is exactly the board's decision conditions (blocking checks not
 * done, plus blocker-severity exceptions), never the procedural or action items
 * that complete after approval. Pure so all of it is testable.
 */
import { outstandingRows as _outstandingRows } from './request-items.js';

// The reconciled OPEN items, spined exactly as the Outstanding Items list does
// (request items carry the document gaps once a round has been raised, so a
// resolved or withdrawn item closes its gap). Returns the rows still open.
export function reconciledOpenItems({ requestItems = [], documentGaps = [], docStatusOf = () => 'outstanding', responseStatuses = [] } = {}) {
  const items = Array.isArray(requestItems) ? requestItems : [];
  const rows = _outstandingRows({
    requestItems: items,
    documentGaps: items.length ? [] : (Array.isArray(documentGaps) ? documentGaps : []),
    docStatusOf, responseStatuses,
  });
  const done = (row) => row.kind === 'item'
    ? ['resolved', 'withdrawn'].includes(row.item && row.item.state)
    : ['received', 'waived', 'not_required'].includes(row.docStatus);
  return (rows || []).filter(r => r && !done(r));
}

// A document is resolved when the officer accepted it (assessment Satisfactory
// or Accepted by MLRO), so a "Requires Clarification" the officer has since
// accepted is not a material exception.
export function isDocResolved(doc) {
  const a = doc && doc.assessment;
  return a === 'Satisfactory' || a === 'Accepted by MLRO';
}
export function materialDocuments(documents = []) {
  return (Array.isArray(documents) ? documents : []).filter(d => d && (d.assessment === 'Requires Clarification' || d.assessment === 'Unsatisfactory') && !isDocResolved(d));
}

// An AML policy blocks only where one is required OF THIS ENTITY: it is
// regulated / AML-supervised, or the firm's own policy mandates one for a client
// of this type. Otherwise the check is procedural (shown, not blocking) and the
// officer can Resolve with a reason.
export function amlPolicyRequired(report = {}, opts = {}) {
  const r = report || {};
  return !!(r.regulatoryStatus && r.regulatoryStatus.regulated)
    || !!(r.amlSupervision && r.amlSupervision.supervised)
    || !!opts.firmMandated;
}
export function amlPolicyDone(report = {}, resolution) {
  if (resolution) return true;
  const ap = (report && report.amlPolicyAssessment) || {};
  return /satisfactory/i.test(String(ap.score || '')) || ap.provided === true;
}

/* ── SOURCE OF WEALTH / FUNDS ACCEPTANCE, DURABLE (v1_745) ───────────────────
 *
 * When the officer accepts "Source of wealth and funds", the acceptance is
 * recorded in collected_data.checkResolutions.funds (its reason, author and
 * date). That record is DURABLE: it lives in collected_data, which every
 * regenerate path read-merges, so a Reprocess or Quick Update preserves it. The
 * transient sof/sowAssessment.score the accept step also stamped onto the report
 * is rewritten by a regenerate (the model re-scores from documents), so every
 * surface, the panel badges, the panel header cue and the rationale fact pack,
 * must derive the displayed status from the durable resolution, not the
 * regenerated score. One acceptance covers BOTH source of wealth and source of
 * funds, matching the single "Source of wealth and funds" control. Pure so the
 * survive-a-Reprocess behaviour is testable.
 */
export function sowSofAcceptance(checkResolutions) {
  const f = (checkResolutions || {}).funds;
  if (!f) return null;
  return { accepted: true, by: f.by || '', at: f.at || '', note: f.note || '' };
}

// The displayed assessment score for source of wealth or funds: "Accepted by
// MLRO" once the officer has accepted (checkResolutions.funds), otherwise the
// model's raw score. withReason appends the officer's recorded reason, for the
// fact pack (so the draft narrates the acceptance and why), never for a badge.
export function acceptedAssessmentScore(rawScore, acceptance, opts = {}) {
  if (acceptance && acceptance.accepted) {
    return opts.withReason
      ? 'Accepted by the MLRO' + (acceptance.note ? ': ' + String(acceptance.note) : '')
      : 'Accepted by MLRO';
  }
  return rawScore || null;
}

/* ── ONE RECONCILED SOURCE FOR THE SIGN OFF DIALOG (v1_746) ──────────────────
 *
 * The sign off dialog listed raw report.documentGaps filtered only by
 * document_tracking, so a case whose gaps were all resolved through their
 * request items (the reconciled spine every other surface uses) still read "N
 * document gaps still outstanding" while the board, the reconciled outstanding
 * and the rationale fact pack all read zero. The dialog must read the SAME
 * reconciled source: the board's decision conditions (blocking) and the noted
 * facts, never raw documentGaps. The document gap checklist tick is derived from
 * the reconciled open items, not left as a free tick. Pure so it is testable.
 *
 * @param reconciledOutstanding blockingOutstanding(checks, exceptions)
 * @param notedItems notedFacts(...)
 * @param openItems reconciledOpenItems(...)
 */
export function signoffOutstanding({ reconciledOutstanding = [], notedItems = [], openItems = [] } = {}) {
  return {
    blocking: Array.isArray(reconciledOutstanding) ? reconciledOutstanding : [],
    noted: Array.isArray(notedItems) ? notedItems : [],
    // The checklist tick "All document gaps resolved, waived or marked not
    // required" is TRUE only when the reconciled open item list is empty.
    documentGapsResolved: (Array.isArray(openItems) ? openItems.length : 0) === 0,
    hasAny: (Array.isArray(reconciledOutstanding) ? reconciledOutstanding.length : 0) > 0
         || (Array.isArray(notedItems) ? notedItems.length : 0) > 0,
  };
}

// The rationale fact pack's outstanding: ONLY the board's decision conditions,
// so it agrees with readyForSignoff. Procedural items (identity, wallet,
// sanctions screening) and action items (document gaps, clarifications) complete
// after or alongside approval and are never listed as conditions on the
// decision.
export function blockingOutstanding(checks = [], exceptions = []) {
  const notDone = (Array.isArray(checks) ? checks : []).filter(c => c && !c.done).map(c => c.q);
  const blockerEx = (Array.isArray(exceptions) ? exceptions : []).filter(e => e && e.severity === 'blocker').map(e => e.label);
  return [...notDone, ...blockerEx];
}

// The NOTED facts for the rationale: open action items and unresolved procedural
// steps. These are facts the draft must state (a document still marked Requires
// Clarification, the client items still outstanding, the procedural steps not yet
// done), never decision conditions, and the file may never be called complete
// while any remain.
export function notedFacts({ materialDocs = [], openItemsCount = 0, procedural = [] } = {}) {
  return [
    ...(Array.isArray(materialDocs) ? materialDocs : []).map(d => `Document "${(d && (d.file_name || d.doc_type)) || 'a document'}" remains marked Requires Clarification and has not yet been accepted by the MLRO.`),
    ...(openItemsCount > 0 ? [`${openItemsCount} item${openItemsCount > 1 ? 's' : ''} still outstanding with the client.`] : []),
    ...(Array.isArray(procedural) ? procedural : []).filter(c => c && !c.done).map(c => `${c.q}: ${c.detail}.`),
  ];
}

// amlSupervision for the fact pack: a recorded "not supervised" determination is
// passed AS a determination, never collapsed to null (which the model read as
// "not recorded"). Supervised passes the body; unassessed passes null.
export function amlSupervisionDetermination(amlSupervision = {}) {
  const s = amlSupervision || {};
  if (s.supervised) return s.sroName || s.supervisoryBody || s.regulator || 'SRO-supervised';
  if (s.assessment || s.supervised === false) return 'Assessed as not supervised';
  return null;
}
